import { ReactLenis } from "lenis/dist/lenis-react";
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from "framer-motion";
import { FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { useRef } from "react";
import { Link, useParams } from "react-router-dom";

const CASE_STUDIES = {
  "shivender-kanwar": {
    heroImage: "https://framerusercontent.com/images/UWPvLAi5YrL7SazOLSfhyPqXk.jpg?width=6000&height=4000",
    heroPosition: "center 50%",
    parallaxImages: [
      { src: "https://i.ytimg.com/vi/OvMlDHgJukk/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLDac_X4SqSM8BNhWPdBdCbe6SI0uA", alt: "YouTube video thumbnail" },
      { src: "https://i.ytimg.com/vi/Yl8hDaU-kfM/hqdefault.jpg?sqp=-oaymwEcCNACELwBSFXyq4qpAw4IARUAAIhCGAFwAcABBg==&rs=AOn4CLBuc-JUxP7xXcfA9DmSwNqnlEbLcA", alt: "Content creation process" },
      { src: "https://i.ytimg.com/vi/loxmgJZKG3E/hqdefault.jpg?sqp=-oaymwEnCNACELwBSFryq4qpAxkIARUAAIhCGAHYAQHiAQoIGBACGAY4AUAB&rs=AOn4CLCuJ0vki1kP8eZcdXMAez0ilcdpsQ", alt: "Video production" }
    ],
    title: "Shivender Kanwar",
    subtitle: "From Zero to 100K+ Subscribers: Building a Military Content Empire",
    challenge: "Shivender Kanwar, an ex-Para SF commando, had incredible stories and expertise but zero digital presence. The challenge was to translate military experience into engaging content that could build trust and authority in the crowded YouTube space.",
    strategy: "We implemented a three-phase approach: First, we conducted deep extraction interviews to capture authentic military stories. Second, we engineered content for maximum retention using our proprietary analysis system. Third, we optimized distribution across YouTube and social platforms with data-driven insights.",
    results: "Within 12 months, we achieved 100K+ subscribers, 10M+ total views, and established Shivender as a leading voice in military content. Average retention rates exceeded 60%, and the channel became a sustainable media asset generating consistent revenue.",
    metrics: [
      { value: "100K+", label: "Subscribers" },
      { value: "10M+", label: "Total Views" },
      { value: "60%", label: "Avg Retention" },
      { value: "12mo", label: "Timeline" },
    ],
    keyLearnings: "This case study proved our thesis: authentic expertise + attention engineering = owned media assets. The success came from treating content as a data problem, not a creative one. Every video was tested, analyzed, and optimized based on retention curves and audience behavior."
  },
  "tech-founder-100k-subscribers": {
    heroImage: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=3270&auto=format&fit=crop",
    parallaxImages: [
      { src: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=3270&auto=format&fit=crop", alt: "Tech Content" },
      { src: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=3270&auto=format&fit=crop", alt: "Strategy Session" },
      { src: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=3270&auto=format&fit=crop", alt: "Analytics" }
    ],
    title: "Tech Founder Series",
    subtitle: "Scaling Technical Thought Leadership",
    challenge: "A technical founder needed to transition from engineering blogs to video content to reach a broader audience and attract top talent.",
    strategy: "We focused on 'Building in Public' - documenting the engineering challenges and solutions in high-fidelity video formats.",
    results: "Achieved 100K subscribers in the niche tech sector, resulting in a 40% increase in inbound engineering applications.",
    metrics: [
      { value: "100K", label: "Subscribers" },
      { value: "40%", label: "Hiring Lift" },
      { value: "500K+", label: "Dev Reach" },
      { value: "6mo", label: "Timeline" },
    ],
    keyLearnings: "Technical audiences value density and authenticity. High production value combined with deep technical content is a winning formula."
  },
  "vc-firm-50m-views": {
    heroImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=3415&auto=format&fit=crop",
    parallaxImages: [
      { src: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=3432&auto=format&fit=crop", alt: "Meeting" },
      { src: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=3270&auto=format&fit=crop", alt: "Building" },
      { src: "https://images.unsplash.com/photo-1556761175-4b46a572b786?q=80&w=3348&auto=format&fit=crop", alt: "Growth" }
    ],
    title: "VC Firm Media Arm",
    subtitle: "50M+ Views for Investment Thesis",
    challenge: "A leading VC firm wanted to build a media moat to attract better deal flow and support portfolio companies.",
    strategy: "Created an educational media arm focusing on market trends, founder stories, and investment thesis deep-dives.",
    results: "Generated 50M+ views across platforms, becoming a primary source of deal flow.",
    metrics: [
      { value: "50M+", label: "Views" },
      { value: "3X", label: "Deal Flow" },
      { value: "15", label: "PortCo Features" },
      { value: "18mo", label: "Timeline" },
    ],
    keyLearnings: "Media is the new leverage for capital. Educational content builds long-term trust with founders."
  },
  "enterprise-2m-arr-content": {
    heroImage: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=3270&auto=format&fit=crop",
    parallaxImages: [
      { src: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=3270&auto=format&fit=crop", alt: "Office" },
      { src: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=3270&auto=format&fit=crop", alt: "Team" },
      { src: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=3270&auto=format&fit=crop", alt: "Work" }
    ],
    title: "Enterprise Revenue Engine",
    subtitle: "$2M ARR Directly from Content",
    challenge: "An enterprise SaaS company wanted to reduce reliance on paid ads and build organic acquisition channels.",
    strategy: "Launched a media brand targeting decision-makers with high-value industry insights and problem-solving content.",
    results: "Attributed $2M in ARR directly to organic content leads within the first year.",
    metrics: [
      { value: "$2M", label: "ARR Generated" },
      { value: "-60%", label: "CAC Reduction" },
      { value: "500+", label: "Qualified Leads" },
      { value: "12mo", label: "Timeline" },
    ],
    keyLearnings: "B2B content doesn't have to be boring. High-quality production and storytelling convert enterprise buyers."
  }
};

export const CaseStudyParallax = () => {
  const { slug } = useParams();
  const data = CASE_STUDIES[slug] || CASE_STUDIES["shivender-kanwar"];

  return (
    <div className="bg-black">
      <ReactLenis
        root
        options={{
          lerp: 0.05,
        }}
      >
        <Nav />
        <Hero data={data} />
        <CaseStudyContent data={data} />
      </ReactLenis>
    </div>
  );
};

const Nav = () => {
  return (
    <nav className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between px-6 py-6 text-white">
      <Link to="/" className="flex items-center gap-3">
        <img 
          src="https://framerusercontent.com/images/mCmSpcpcYCZJTSJSoearkxxG0FQ.png?width=236&height=240" 
          alt="AttentionLabs" 
          className="h-10 w-10"
        />
        <span className="font-bold text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          AttentionLabs
        </span>
      </Link>
      <Link
        to="/"
        className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <FiArrowLeft /> BACK TO HOME
      </Link>
    </nav>
  );
};

const SECTION_HEIGHT = 1500;

const Hero = ({ data }) => {
  return (
    <div
      style={{ height: `calc(${SECTION_HEIGHT}px + 100vh)` }}
      className="relative w-full"
    >
      <CenterImage src={data.heroImage} position={data.heroPosition} />
      <ParallaxImages images={data.parallaxImages} />
      <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-b from-black/0 to-black" />
    </div>
  );
};

const CenterImage = ({ src, position }) => {
  const { scrollY } = useScroll();

  const clip1 = useTransform(scrollY, [0, 1500], [25, 0]);
  const clip2 = useTransform(scrollY, [0, 1500], [75, 100]);

  const clipPath = useMotionTemplate`polygon(${clip1}% ${clip1}%, ${clip2}% ${clip1}%, ${clip2}% ${clip2}%, ${clip1}% ${clip2}%)`;

  const backgroundSize = useTransform(
    scrollY,
    [0, SECTION_HEIGHT + 500],
    ["170%", "100%"]
  );
  const opacity = useTransform(
    scrollY,
    [SECTION_HEIGHT, SECTION_HEIGHT + 500],
    [1, 0]
  );

  return (
    <motion.div
      className="sticky top-0 h-screen w-full"
      style={{
        clipPath,
        backgroundSize,
        opacity,
        backgroundImage: `url(${src})`,
        backgroundPosition: position || "center 30%",
        backgroundRepeat: "no-repeat",
      }}
    />
  );
};

const ParallaxImages = ({ images }) => {
  return (
    <div className="mx-auto max-w-5xl px-4 pt-[200px]">
      <ParallaxImg
        src={images[0].src}
        alt={images[0].alt}
        start={-200}
        end={200}
        className="w-1/3 rounded-lg"
      />
      <ParallaxImg
        src={images[1].src}
        alt={images[1].alt}
        start={200}
        end={-250}
        className="mx-auto w-2/3 rounded-lg"
      />
      <ParallaxImg
        src={images[2].src}
        alt={images[2].alt}
        start={-200}
        end={200}
        className="ml-auto w-1/3 rounded-lg"
      />
    </div>
  );
};

const ParallaxImg = ({ className, alt, src, start, end }) => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: [`${start}px end`, `end ${end * -1}px`],
  });

  const opacity = useTransform(scrollYProgress, [0.75, 1], [1, 0]);
  const scale = useTransform(scrollYProgress, [0.75, 1], [1, 0.85]);

  const y = useTransform(scrollYProgress, [0, 1], [start, end]);
  const transform = useMotionTemplate`translateY(${y}px) scale(${scale})`;

  return (
    <motion.img
      src={src}
      alt={alt}
      className={className}
      ref={ref}
      style={{ transform, opacity }}
    />
  );
};

const CaseStudyContent = ({ data }) => {
  return (
    <section className="mx-auto max-w-5xl px-4 py-48 text-white">
      <motion.h1
        initial={{ y: 48, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ ease: "easeInOut", duration: 0.75 }}
        className="mb-8 text-5xl md:text-7xl font-bold text-zinc-50"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {data.title}
      </motion.h1>
      
      <motion.p
        initial={{ y: 48, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ ease: "easeInOut", duration: 0.75, delay: 0.1 }}
        className="mb-20 text-xl text-zinc-400"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {data.subtitle}
      </motion.p>

      <CaseStudySection 
        title="The Challenge"
        content={data.challenge}
      />

      <CaseStudySection 
        title="The Strategy"
        content={data.strategy}
      />

      <CaseStudySection 
        title="The Results"
        content={data.results}
      />

      <MetricsGrid metrics={data.metrics} />

      <CaseStudySection 
        title="Key Learnings"
        content={data.keyLearnings}
      />

      <motion.div
        initial={{ y: 48, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ ease: "easeInOut", duration: 0.75 }}
        className="mt-20 text-center"
      >
        <Link
          to="/contact"
          className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all text-lg"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Start Your Protocol Sprint
        </Link>
      </motion.div>
    </section>
  );
};

const CaseStudySection = ({ title, content }) => {
  return (
    <motion.div
      initial={{ y: 48, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      transition={{ ease: "easeInOut", duration: 0.75 }}
      className="mb-16"
    >
      <h2 
        className="mb-4 text-3xl font-bold text-zinc-50"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {title}
      </h2>
      <p 
        className="text-lg text-zinc-400 leading-relaxed"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {content}
      </p>
    </motion.div>
  );
};

const MetricsGrid = ({ metrics }) => {
  return (
    <motion.div
      initial={{ y: 48, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      transition={{ ease: "easeInOut", duration: 0.75 }}
      className="my-20 grid grid-cols-2 md:grid-cols-4 gap-8"
    >
      {metrics.map((metric, idx) => (
        <div key={idx} className="border border-white/10 rounded-lg p-6 text-center">
          <div 
            className="text-4xl font-bold mb-2"
            style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}
          >
            {metric.value}
          </div>
          <div 
            className="text-sm text-zinc-400"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {metric.label}
          </div>
        </div>
      ))}
    </motion.div>
  );
};

export default CaseStudyParallax;
